import type { Brand } from '../brand';

/** Opaque entity identifiers. Only this module (and the import parser) mints them. */
export type LoadId = Brand<string, 'LoadId'>;
export type RailId = Brand<string, 'RailId'>;
export type RoomId = Brand<string, 'RoomId'>;
export type DaliBridgeId = Brand<string, 'DaliBridgeId'>;

const ID_PREFIX = {
  LOAD: 'load',
  RAIL: 'rail',
  ROOM: 'room',
  BRIDGE: 'bridge',
} as const;

const ID_SUFFIX = {
  RADIX: 36,
  RANDOM_START: 2,
  RANDOM_END: 10,
} as const;

/**
 * `crypto.randomUUID` is only available in a secure context, and kiosk tablets
 * are often served over plain http on the local network.
 */
const canUseRandomUuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';

const randomSuffix = (): string =>
  canUseRandomUuid
    ? crypto.randomUUID()
    : `${Date.now().toString(ID_SUFFIX.RADIX)}${Math.random()
        .toString(ID_SUFFIX.RADIX)
        .slice(ID_SUFFIX.RANDOM_START, ID_SUFFIX.RANDOM_END)}`;

export const newLoadId = (): LoadId => `${ID_PREFIX.LOAD}_${randomSuffix()}` as LoadId;
export const newRailId = (): RailId => `${ID_PREFIX.RAIL}_${randomSuffix()}` as RailId;
export const newRoomId = (): RoomId => `${ID_PREFIX.ROOM}_${randomSuffix()}` as RoomId;
export const newDaliBridgeId = (): DaliBridgeId =>
  `${ID_PREFIX.BRIDGE}_${randomSuffix()}` as DaliBridgeId;

/**
 * Adopts an id that came from outside (stored JSON). Only the decoder should
 * need these — everywhere else takes an id that was minted here.
 */
export const asLoadId = (value: string): LoadId => value as LoadId;
export const asRailId = (value: string): RailId => value as RailId;
export const asRoomId = (value: string): RoomId => value as RoomId;
export const asDaliBridgeId = (value: string): DaliBridgeId => value as DaliBridgeId;
